import React, { ReactNode, useEffect } from "react"
import { useForm, Control } from "react-hook-form"
import {
  BadgeElementType,
  BadgeQRFormat,
  BorderStyle,
  TransformStyle,
  ColorValue,
  BadgeTextData,
  BadgeFieldData,
  BadgeImageData,
  BadgeLineData,
  BadgeShapeData,
  BadgeQRData,
} from "sharedTypes/badge"
import AutoSave from "../../AutoSave"

/**
 * Form values type - a flattened version of BadgeElement with its data properties
 * spread at the top level for easier form field management
 */
export type BadgeElementFormValues = {
  selectedBlockType: BadgeElementType
  x: number
  y: number
  width: number
  height: number
  zIndex: number
  fill?: ColorValue
  border?: BorderStyle
  transforms?: TransformStyle
  // Optional here so react-hook-form can resolve it as a valid path
  // regardless of which element data variant is active
  format?: BadgeQRFormat
} & (
  | BadgeTextData
  | BadgeFieldData
  | BadgeImageData
  | BadgeLineData
  | BadgeShapeData
  | BadgeQRData
)

type Props = {
  initialValues: BadgeElementFormValues
  children: (args: { control: Control<BadgeElementFormValues> }) => ReactNode
  selectedElementId?: string
}

const BadgeSidePanelForm = ({
  initialValues,
  children,
  selectedElementId,
}: Props) => {
  const { control, reset, getValues } = useForm<BadgeElementFormValues>({
    defaultValues: initialValues,
    mode: "onChange",
  })

  // The form follows the element when it changes from outside the form (the canvas, undo,
  // another element selected); the form's own edits come back identical and change nothing
  const elementValues = JSON.stringify(initialValues)
  useEffect(() => {
    if (JSON.stringify(getValues()) !== elementValues) reset(initialValues)
  }, [selectedElementId, elementValues])

  return (
    <form>
      <AutoSave
        control={control}
        elementId={selectedElementId}
        onUndone={() => reset(initialValues)}
      />
      {children({ control })}
    </form>
  )
}

export default BadgeSidePanelForm
